import { describeMutationError } from "@api/mutationErrors";
import { useSetRoomBaseline } from "@rooms/roomsQueries";
import {
  Button,
  Checkbox,
  Dialog,
  FieldError,
} from "@scribenote/brian-design-system";
import { useState } from "react";

import { StillFrame } from "./StillFrame";

/**
 * Save the shown still as a room's empty-room baseline. The browser sends the
 * room and the shown still's reference; brian copies its own stored still, so
 * polling cannot swap the picture between looking and saving.
 */
export const BaselineCapture = ({
  roomId,
  url,
  reference,
  onClose,
}: {
  roomId: string;
  url: string;
  reference: string;
  onClose: () => void;
}) => {
  const save = useSetRoomBaseline();
  const [empty, setEmpty] = useState(false);
  const saved = save.isSuccess;

  return (
    <Dialog
      open
      onClose={onClose}
      size="md"
      title="Set the empty-room baseline"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {saved ? "Done" : "Cancel"}
          </Button>
          {!saved && (
            <Button
              variant="accent"
              disabled={!empty || save.isPending}
              onClick={() =>
                save.mutate({ roomUuid: roomId, stillReference: reference })
              }
            >
              Save baseline
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-ink-body">
          Brian compares every camera picture with this one. Make sure nobody
          and no animal is in the room.
        </p>
        <StillFrame url={url} />
        <Checkbox
          checked={empty}
          disabled={saved}
          onChange={(event) => setEmpty(event.target.checked)}
          label="The room is empty"
        />
        {save.isError && (
          <FieldError>
            {describeMutationError(save.error, "save the baseline")}
          </FieldError>
        )}
        {saved && <p className="text-sm text-ink-body">Saved.</p>}
      </div>
    </Dialog>
  );
};
